import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { getCompanies, Company, CompanyId } from '../services/accountingService';

interface CompanyContextType {
    companies: Company[];
    selectedCompanyId: CompanyId;
    selectedCompany: Company | null;
    setSelectedCompanyId: (companyId: CompanyId) => void;
    isLoading: boolean;
    error: string | null;
    refreshCompanies: () => Promise<void>;
}

const CompanyContext = createContext<CompanyContextType | undefined>(undefined);

const STORAGE_KEY = 'accounting_selected_company';

export const CompanyProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [companies, setCompanies] = useState<Company[]>([]);
    const [selectedCompanyId, setSelectedCompanyId] = useState<CompanyId>(() => {
        // Try to get from localStorage
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved) {
            return saved as CompanyId;
        }
        return 'primary';
    });
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchCompanies = async () => {
        try {
            setIsLoading(true);
            setError(null);
            const data = await getCompanies(true);
            setCompanies(data);
        } catch (err: any) {
            console.error('Failed to fetch companies:', err);
            setError(err.message || 'Failed to fetch companies');
            // Set default companies if API fails
            setCompanies([
                {
                    _id: '1',
                    code: 'primary',
                    name: 'UAE Company (VAT)',
                    shortName: 'UAE (VAT)',
                    displayName: 'REM APP PORTAL L.L.C',
                    logoUrl: null,
                    logoFileName: null,
                    country: 'United Arab Emirates',
                    currency: 'AED',
                    vatEnabled: true,
                    vatPercentage: 5,
                    settingsProfile: 'primaryCompany',
                    isActive: true,
                    sortOrder: 1,
                    stripeReconEnabled: false,
                    stripeClearingAccountId: null
                },
                {
                    _id: '2',
                    code: 'secondary',
                    name: 'UAE Company (Non-VAT)',
                    shortName: 'UAE (Non-VAT)',
                    displayName: 'REM APP Services',
                    logoUrl: null,
                    logoFileName: null,
                    country: 'United Arab Emirates',
                    currency: 'AED',
                    vatEnabled: false,
                    vatPercentage: 0,
                    settingsProfile: 'secondaryCompany',
                    isActive: true,
                    sortOrder: 2,
                    stripeReconEnabled: false,
                    stripeClearingAccountId: null
                },
                {
                    _id: '3',
                    code: 'third',
                    name: 'Pakistan Company',
                    shortName: 'Pakistan',
                    displayName: 'REM APP Pakistan',
                    logoUrl: null,
                    logoFileName: null,
                    country: 'Pakistan',
                    currency: 'PKR',
                    vatEnabled: false,
                    vatPercentage: 0,
                    settingsProfile: 'thirdCompany',
                    isActive: true,
                    sortOrder: 3,
                    stripeReconEnabled: false,
                    stripeClearingAccountId: null
                }
            ]);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchCompanies();
    }, []);

    // Save to localStorage when selection changes
    useEffect(() => {
        localStorage.setItem(STORAGE_KEY, selectedCompanyId);
    }, [selectedCompanyId]);

    const selectedCompany = companies.find(c => c.code === selectedCompanyId) || null;

    const value: CompanyContextType = {
        companies,
        selectedCompanyId,
        selectedCompany,
        setSelectedCompanyId,
        isLoading,
        error,
        refreshCompanies: fetchCompanies
    };

    return (
        <CompanyContext.Provider value={value}>
            {children}
        </CompanyContext.Provider>
    );
};

export const useCompany = (): CompanyContextType => {
    const context = useContext(CompanyContext);
    if (context === undefined) {
        throw new Error('useCompany must be used within a CompanyProvider');
    }
    return context;
};

export default CompanyContext;
